import { useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { useConfig } from '@/api/queries';
import { useAuth } from '@/stores/auth';
import { serverClock } from '@/lib/clock';
import type { VideoToken } from '@/api/types';
import type { FourAction, FourView, SignalTokenView, ThemeCheck, ThemeDraft, UsInvite, UsInvitePreview, UsTheme, UsView, WatchAction, WatchView } from './types';

/** SPEC_V15 endpoints. Every one is 404 feature_disabled while the `us` flag is off. */
const req = api.request;

export const usApi = {
  get: () => req<UsView>('/v1/us'),
  update: (patch: { name?: string; since?: string }) => req<UsView>('/v1/us', { method: 'PATCH', body: patch }),
  leave: () => req<void>('/v1/us', { method: 'DELETE', body: { confirm: 'LEAVE' } }),
  setMode: (on: boolean) => req<UsView>('/v1/us/mode', { method: 'PUT', body: { on } }),
  createInvite: () => req<UsInvite>('/v1/us/invite', { method: 'POST' }),
  cancelInvite: () => req<void>('/v1/us/invite', { method: 'DELETE' }),
  preview: (code: string) => req<UsInvitePreview>(`/v1/us/invites/${encodeURIComponent(code)}`),
  accept: (code: string) => req<UsView>(`/v1/us/invites/${encodeURIComponent(code)}/accept`, { method: 'POST' }),
  generateTheme: (prompt: string) => req<ThemeDraft>('/v1/us/theme/generate', { method: 'POST', body: { prompt } }),
  checkTheme: (css: string) => req<ThemeCheck>('/v1/us/theme/check', { method: 'POST', body: { css } }),
  saveTheme: (t: { css: string; name?: string; prompt?: string; source: UsTheme['source'] }) => req<UsView>('/v1/us/theme', { method: 'PUT', body: t }),
  useTheme: (id: string | null) => req<UsView>('/v1/us/theme/current', { method: 'PUT', body: { id } }),
  themes: () => req<{ themes: UsTheme[] }>('/v1/us/themes'),
  callToken: () => req<VideoToken>('/v1/us/call/token', { method: 'POST' }),
  ring: (to: 'call' | 'watch') => req<void>('/v1/us/call/ring', { method: 'POST', body: { to } }),
  watch: () => req<WatchView>('/v1/us/watch').then(sampled),
  act: (a: WatchAction) => req<WatchView>('/v1/us/watch/actions', { method: 'POST', body: a }).then(sampled),
  four: () => req<FourView>('/v1/us/four'),
  fourAct: (a: FourAction) => req<FourView>('/v1/us/four/actions', { method: 'POST', body: a }),
  /** The Popin Watch handshake token (SPEC_V15 §5.4), handed to the extension. */
  signalToken: () => req<SignalTokenView>('/v1/us/signal/token', { method: 'POST' }),
};

function sampled(v: WatchView): WatchView {
  if (v?.server_time) serverClock.sample(v.server_time);
  return v;
}

export const usKeys = {
  us: ['us'] as const,
  themes: ['us', 'themes'] as const,
  watch: ['us', 'watch'] as const,
  four: ['us', 'four'] as const,
  invite: (code: string) => ['us', 'invite', code] as const,
};

/**
 * The flag (absent = on, for older servers that don't report it), or a
 * preview while it's off: admins and the people they pair with (SPEC_V15 §6).
 */
export function useUsEnabled(): boolean {
  const config = useConfig();
  const preview = useAuth((s) => !!s.me?.us_preview);
  return config.data?.features.us !== false || preview;
}

export const useUs = (enabled = true) => {
  const on = useUsEnabled();
  const authed = useAuth((s) => s.status === 'authed' && !!s.me?.onboarded);
  return useQuery({ queryKey: usKeys.us, queryFn: usApi.get, enabled: enabled && on && authed, staleTime: 15_000, refetchInterval: 60_000, retry: false });
};

export const useUsThemes = (enabled = true) => useQuery({ queryKey: usKeys.themes, queryFn: () => usApi.themes().then((r) => r.themes), enabled, retry: false });

/** Only accept a watch state at least as new as what we hold. */
export function newerWatch(prev: WatchView | undefined, next: WatchView): WatchView {
  if (!prev || prev.space_id !== next.space_id) return next;
  return next.watch.v >= prev.watch.v ? next : prev;
}

export const useWatchState = (enabled = true) =>
  useQuery({ queryKey: usKeys.watch, queryFn: usApi.watch, enabled, staleTime: Infinity, refetchInterval: 30_000, retry: false });

/** Only accept a game state at least as new as what we hold. */
export function newerFour(prev: FourView | undefined, next: FourView): FourView {
  if (!prev || prev.space_id !== next.space_id) return next;
  return next.four.v >= prev.four.v ? next : prev;
}

export const useFourState = (enabled = true) =>
  useQuery({ queryKey: usKeys.four, queryFn: usApi.four, enabled, staleTime: Infinity, refetchInterval: 30_000, retry: false });
